import * as R from "ramda";
import * as yup from "yup";
import Fuse from "fuse.js";

import { CANDIDATE_LISTS, STRICT_FILTERS } from "../constants/opportunity";

import { getCandidateScreeningStatus } from "./candidates";
import { removeAccents } from "./strings";
import storageUtils from "./storageUtils";
import {
  decodeQueryString,
  parseQueryArrayWith,
  parseQueryStringArray,
  parseQueryStringAsNumber
} from "./queryStrings";

export const CANDIDATES_PER_PAGE = 36;

// Use schema to validate query string or sessionStorage filters
// (prevent initializing state with wrong values)
const listFiltersSchema = yup
  .object()
  .shape({
    searchText: yup.string(),
    sort: yup.string().oneOf(["score", "date", "fullname", ""]),
    registered: yup.string().oneOf(["true", "false", ""]),
    strictFilters: yup
      .array()
      .of(yup.string().oneOf(R.keys(STRICT_FILTERS.options))),
    referral: yup.boolean(),
    previousmatch: yup.boolean(),
    languages: yup.array().of(yup.string()),
    skills: yup.array().of(yup.string()),
    chatbotTags: yup.string(),
    tags: yup.string(),
    agerange: yup
      .array()
      .min(2)
      .max(2)
      .of(
        yup
          .number()
          .min(16)
          .max(65)
      ),
    country: yup.array().of(yup.string()),
    province: yup.array().of(yup.number()),
    district: yup.array().of(yup.number()),
    education: yup.array().of(yup.string()),
    workexperience: yup
      .array()
      .min(2)
      .max(2)
      .of(
        yup
          .number()
          .min(0)
          .max(10)
      ),
    expectedsalary: yup
      .array()
      .min(2)
      .max(2)
      .of(yup.number().nullable()),
    page: yup.number().min(1),
    fieldsofstudy: yup.array().of(yup.string()),
    professions: yup.array().of(yup.string()),
    tagprofile: yup.array().of(yup.string()),
    applydate: yup.array().of(yup.string()),
    screeningstatus: yup
      .string()
      .oneOf(["completed", "pending", "passed", "disapproved", ""]),
    score: yup
      .array()
      .min(2)
      .max(2)
      .of(
        yup
          .number()
          .min(0)
          .max(1)
      )
  })
  .noUnknown(true);

// Change version to invalidate previous session storage structure
export const getFiltersSessionStorageKey = filterKey => `${filterKey}.v3.7`;

export const deleteOpportunityStoredFilters = opportunity =>
  storageUtils.deleteItems(
    sessionStorage,
    R.filter(
      R.includes(R.prop("id", opportunity), R.__),
      R.keys(sessionStorage)
    )
  );

export const getStrictFiltersForOpportunity = opportunity =>
  STRICT_FILTERS.order
    .map(option => ({
      label: R.prop(option, STRICT_FILTERS.labels)({ opportunity }),
      value: R.prop(option, R.invertObj(STRICT_FILTERS.options))
    }))
    .filter(({ label }) => Boolean(label));

export const getServerFilters = filters => {
  return {
    ...(filters.strictFilters && filters.strictFilters.length > 0
      ? { filters: filters.strictFilters }
      : {}),
    ...(filters.searchText !== ""
      ? {
          searchText: filters.searchText
        }
      : {}),
    ...(filters.registered !== ""
      ? {
          registered: filters.registered
        }
      : {}),
    ...(filters.chatbotTags
      ? {
          chatbottags: filters.chatbotTags
        }
      : {}),
    ...(filters.tags
      ? {
          tags: filters.tags
        }
      : {}),
    ...(filters.languages && filters.languages.length > 0
      ? {
          languages: filters.languages
        }
      : {}),
    ...(filters.agerange
      ? {
          agerange: filters.agerange
        }
      : {}),
    ...(filters.country && filters.country.length > 0
      ? {
          country: filters.country
        }
      : {}),
    ...(filters.province && filters.province.length > 0
      ? {
          province: filters.province
        }
      : {}),
    ...(filters.district && filters.district.length > 0
      ? {
          district: filters.district
        }
      : {}),
    ...(filters.education && filters.education.length > 0
      ? {
          education: filters.education
        }
      : {}),
    ...(filters.skills && filters.skills.length > 0
      ? {
          skills: filters.skills
        }
      : {}),
    ...(filters.workexperience
      ? {
          workexperience: filters.workexperience
        }
      : {}),
    ...(filters.expectedsalary &&
    (filters.expectedsalary[0] || filters.expectedsalary[1])
      ? {
          expectedsalary: filters.expectedsalary
        }
      : {}),
    ...(filters.page
      ? {
          skip: CANDIDATES_PER_PAGE * (filters.page - 1)
        }
      : {}),
    referral: filters.referral,
    previousmatch: filters.previousmatch,
    ...(filters.fieldsofstudy && filters.fieldsofstudy.length > 0
      ? {
          fieldsofstudy: filters.fieldsofstudy
        }
      : {}),
    ...(filters.professions && filters.professions.length > 0
      ? {
          professions: filters.professions
        }
      : {}),
    ...(filters.tagprofile && filters.tagprofile.length > 0
      ? {
          tagprofile: filters.tagprofile
        }
      : {}),
    ...(filters.screeningstatus !== ""
      ? {
          screeningstatus: filters.screeningstatus
        }
      : {}),
    ...(filters.score
      ? {
          score: filters.score
        }
      : {}),
    ...(filters.applydate
      ? {
          applydate: filters.applydate
        }
      : {}),
    ...(filters.sort !== ""
      ? {
          sort: filters.sort
        }
      : {})
  };
};

const getFiltersFromQuery = (defaultFilters, searchQuery, filtersByList) => {
  const queryStringFilters = decodeQueryString(searchQuery, {
    registered: String,
    searchText: String,
    sort: String,
    screeningstatus: String,
    previousmatch: Boolean,
    referral: Boolean,
    languages: parseQueryStringArray,
    country: parseQueryStringArray,
    province: parseQueryArrayWith(parseQueryStringAsNumber),
    district: parseQueryArrayWith(parseQueryStringAsNumber),
    education: parseQueryStringArray,
    expectedsalary: parseQueryArrayWith(parseQueryStringAsNumber),
    strictFilters: parseQueryStringArray,
    agerange: parseQueryArrayWith(parseQueryStringAsNumber),
    skills: parseQueryStringArray,
    workexperience: parseQueryArrayWith(parseQueryStringAsNumber),
    page: parseQueryStringAsNumber,
    fieldsofstudy: parseQueryStringArray,
    professions: parseQueryStringArray,
    tagprofile: parseQueryStringArray,
    score: parseQueryArrayWith(parseQueryStringAsNumber),
    applydate: parseQueryStringArray
  });

  try {
    listFiltersSchema.validateSync(queryStringFilters, {
      strict: true // prevent coersion
    });

    return [
      {
        ...defaultFilters,
        // Fix empty array not in query string when using qs.stringify (if strictFilters key is not present default to [])
        ...(filtersByList.strictFilters && !R.isEmpty(queryStringFilters)
          ? { strictFilters: [] }
          : {}),
        ...queryStringFilters
      },
      { exitPipeline: true }
    ];
  } catch (error) {
    console.error(error);
    return [defaultFilters, { exitPipeline: false }];
  }
};

const getFiltersFromSessionStorage = (defaultFilters, filterKey) => {
  try {
    const storedFilters = storageUtils.getItem(
      sessionStorage,
      getFiltersSessionStorageKey(filterKey)
    );

    listFiltersSchema.validateSync(storedFilters, { strict: true });

    return [
      {
        ...defaultFilters,
        ...storedFilters
      }
    ];
  } catch (error) {
    return [defaultFilters];
  }
};

export const getFiltersForList = ({
  filterKey,
  filtersUsed,
  //opportunity,
  searchQuery,
  onlyServerFilters = false
}) => {
  const defaultFilters = {
    // ...(filtersUsed.strictFilters
    //   ? {
    //       strictFilters: opportunity
    //         ? R.pluck("value", getStrictFiltersForOpportunity(opportunity))
    //         : []
    //     }
    //   : { strictFilters: [] }),
    ...(filtersUsed.strictFilters ? { strictFilters: [] } : {}),
    ...(filtersUsed.searchText ? { searchText: "" } : {}),
    ...(filtersUsed.registered ? { registered: "" } : { registered: "" }),
    ...(filtersUsed.referral ? { referral: false } : {}),
    ...(filtersUsed.previousmatch ? { previousmatch: false } : {}),
    ...(filtersUsed.chatbotTags ? { chatbotTags: "" } : {}),
    ...(filtersUsed.tags ? { tags: "" } : {}),
    ...(filtersUsed.country ? { country: [] } : {}),
    ...(filtersUsed.province ? { province: [] } : {}),
    ...(filtersUsed.district ? { district: [] } : {}),
    ...(filtersUsed.languages ? { languages: [] } : {}),
    ...(filtersUsed.education ? { education: [] } : {}),
    ...(filtersUsed.agerange ? { agerange: [16, 65] } : {}),
    ...(filtersUsed.skills ? { skills: [] } : {}),
    ...(filtersUsed.page ? { page: 1 } : {}),
    ...(filtersUsed.workexperience ? { workexperience: [0, 10] } : {}),
    ...(filtersUsed.expectedsalary ? { expectedsalary: [0, null] } : {}),
    ...(filtersUsed.fieldsofstudy ? { fieldsofstudy: [] } : {}),
    ...(filtersUsed.professions ? { professions: [] } : {}),
    ...(filtersUsed.tagprofile ? { tagprofile: [] } : {}),
    ...(filtersUsed.applydate ? { applydate: [] } : {}),
    ...(filtersUsed.screeningstatus ? { screeningstatus: "" } : {}),
    ...(filtersUsed.score ? { score: [0, 1] } : {}),
    ...(filtersUsed.sort ? { sort: "score" } : {})
  };

  const [filters] = R.pipe(
    // 1rst try to get filters from search query (if we find them exit pipeline)
    R.when(
      () => searchQuery,
      () => getFiltersFromQuery(defaultFilters, searchQuery, filtersUsed)
    ),
    // 2nd try to get filters from session storage
    R.when(
      ([, { exitPipeline } = {}]) => !exitPipeline,
      () => getFiltersFromSessionStorage(defaultFilters, filterKey)
    )
  )([defaultFilters, { exitPipeline: false }]);

  return onlyServerFilters ? getServerFilters(filters) : filters;
};

const filterBySearch = (candidates, filters) => {
  const searchText = R.propOr("", "searchText", filters);

  if (!searchText) return candidates;

  const processedCandidates = candidates.map(candidate => {
    const current = R.pipe(
      R.propOr([], "lastPositions"),
      R.find(R.prop("current"))
    )(candidate);

    const lastPosition = R.pipe(
      R.propOr([], "lastPositions"),
      R.sortBy(R.prop("end")),
      R.last
    )(candidate);

    return {
      ...candidate,
      _fullname: removeAccents(
        candidate.fullname || `${candidate.first} ${candidate.last}`
      ),
      _lastPositionTitle: removeAccents(
        R.propOr("", ["title"], current || lastPosition)
      ),
      _lastPositionCompany: removeAccents(
        R.propOr("", ["company"], current || lastPosition)
      ),
      _addressName: removeAccents(R.pathOr("", ["address", "name"], candidate)),
      _email: candidate.email || candidate.emailAddress || ""
    };
  });

  const fuse = new Fuse(processedCandidates, {
    threshold: 0.2,
    isCaseSensitive: true,
    minMatchCharLength: 2,
    keys: [
      {
        name: "_fullname",
        weight: 0.2
      },
      {
        name: "_email",
        weight: 0.2
      },
      {
        name: "_lastPositionTitle",
        weight: 0.2
      },
      {
        name: "_lastPositionCompany",
        weight: 0.2
      },
      {
        name: "_addressName",
        weight: 0.2
      }
    ]
  });

  return R.map(
    R.omit([
      "_fullname",
      "_lastPositionTitle",
      "_lastPositionCompany",
      "_addressName"
    ]),
    fuse.search(removeAccents(searchText))
  );
};

const filterBySource = (candidates, filters) => {
  const registered = filters.registered;

  if (R.equals(registered, "")) return candidates;

  return R.filter(candidate => {
    const isRegistered = R.propOr(true, "isRegistered", candidate);
    return (
      (R.equals(registered, "true") && isRegistered) ||
      (R.equals(registered, "false") && !isRegistered)
    );
  }, candidates);
};

const filterByScreeningStatus = (candidates, filters, opportunity) => {
  const screeningStatus = filters.screeningstatus;

  if (!screeningStatus) return candidates;

  return R.filter(
    candidate =>
      getCandidateScreeningStatus(candidate, opportunity) === screeningStatus,
    candidates
  );
};

export const filterCandidates = (
  candidates,
  filters,
  serverFiltersList = false,
  list,
  opportunity
) => {
  let result = candidates;

  if (
    (serverFiltersList && !R.includes("searchText", serverFiltersList)) ||
    [CANDIDATE_LISTS.selected, CANDIDATE_LISTS.rejected].includes(list)
  ) {
    result = filterBySearch(result, filters);
  }

  // TODO revert this if necessary
  if (serverFiltersList && !R.includes("registered", serverFiltersList)) {
    result = filterBySource(result, filters);
  }

  result = filterByScreeningStatus(result, filters, opportunity);

  return result;
};
